import { useCallback } from "react";

import { t } from "i18n/i18n";
import { useCapabilities } from "store/capability-hooks";
import { useAppSelector } from "store/index";
import { selectCurrentClassUserId, selectMaxAvActiveParticipants } from "store/selectors";
import {
  selectIsParticipantBarVisibleClassUserId,
  selectIsPublishedAudioEnabled,
  selectIsVirtualUserPresentInClass,
} from "store/selectors/pubsub-virtual-class-state";
import { useAvPubsubMessaging } from "utils/av";
import { isAvLimitActiveParticipantsEnabled } from "utils/waffle-flags";

export const usePinParticipantTools = (classUserId: string | null | undefined = "") => {
  const { sendPubsubVirtualClassStateMessage } = useAvPubsubMessaging();
  const maxAvActiveParticipants = useAppSelector(selectMaxAvActiveParticipants);
  const currentClassUserId = useAppSelector(selectCurrentClassUserId);
  const isAvLimitActiveParticipantsEnabledFlag = isAvLimitActiveParticipantsEnabled();
  const { canAuthorSection } = useCapabilities();
  const isVirtualUserPresentInClass = useAppSelector((state) =>
    selectIsVirtualUserPresentInClass(state, classUserId ?? ""),
  );
  const isParticipantVisibleInBar = useAppSelector((state) =>
    selectIsParticipantBarVisibleClassUserId(state, classUserId),
  );

  const onPinClassUserIdList = useCallback(
    (newClassUserIds: string[]) => {
      if (!canAuthorSection || !currentClassUserId || newClassUserIds.length === 0) return;

      const shouldLimit =
        isAvLimitActiveParticipantsEnabledFlag && newClassUserIds.length >= maxAvActiveParticipants;
      const newClassUserIdsLimited = shouldLimit
        ? newClassUserIds
            .filter((id) => id !== currentClassUserId)
            .slice(0, maxAvActiveParticipants - 1)
            .concat(currentClassUserId)
        : newClassUserIds;

      sendPubsubVirtualClassStateMessage({
        action: "pin-class-users",
        class_user_ids: newClassUserIdsLimited,
      });
    },
    [
      canAuthorSection,
      currentClassUserId,
      isAvLimitActiveParticipantsEnabledFlag,
      maxAvActiveParticipants,
      sendPubsubVirtualClassStateMessage,
    ],
  );

  const pinParticipantDisabledText = !isVirtualUserPresentInClass
    ? t("user_options_menu.view_participant_not_available")
    : isParticipantVisibleInBar
      ? t("user_options_menu.view_participant_already_visible")
      : undefined;

  return { onPinClassUserIdList, pinParticipantDisabledText };
};

export const useMuteParticipantTools = (userId: string, classUserId: string | null) => {
  const { sendPubsubVirtualClassStateMessage } = useAvPubsubMessaging();
  const { canAuthorSection } = useCapabilities();
  const isVirtualUserPresentInClass = useAppSelector((state) =>
    selectIsVirtualUserPresentInClass(state, classUserId ?? ""),
  );
  const isAudioEnabled = useAppSelector((state) =>
    selectIsPublishedAudioEnabled(state, classUserId ?? ""),
  );
  const onMuteParticipant = useCallback(() => {
    if (!isAudioEnabled || !canAuthorSection || !classUserId || !userId) return;
    sendPubsubVirtualClassStateMessage({
      action: "mute-users",
      user_ids: [userId],
    });
  }, [isAudioEnabled, canAuthorSection, classUserId, userId, sendPubsubVirtualClassStateMessage]);
  const muteParticipantDisabledText = !isVirtualUserPresentInClass
    ? t("user_options_menu.mute_participant_not_available")
    : !isAudioEnabled
      ? t("user_options_menu.mute_participant_already_muted")
      : undefined;
  return { onMuteParticipant, muteParticipantDisabledText };
};
